import { t } from "@travily/i18n";
import { ButtonDock } from "@ui/components/ButtonDock";
import { Dialog } from "@ui/components/Dialog";
import { Field, useField } from "@ui/components/Field";
import { Icon } from "@ui/components/Icon";
import { StandardButton } from "@ui/components/StandardButton";
import { StandardTextArea } from "@ui/components/StandardTextArea";
import { StandardTextField } from "@ui/components/StandardTextField";
import { RatingInput } from "./RatingInput";

export interface ReviewModalProps {
  close: () => void;
  score: number | null;
  body: string;
  onScoreChange: (value: number) => void;
  onBodyChange: (value: string) => void;
  onSubmit: () => void;
  isPending: boolean;
  scoreError?: string;
  bodyError?: string;
  // Guest review-from-receipt flow: collect a display name when the buyer's
  // account has none (otherwise their email local-part would be shown). Submit
  // is disabled until the required name is filled, so no inline error is needed.
  showName?: boolean;
  name?: string;
  onNameChange?: (value: string) => void;
}

function RatingField({ onChange, value }: { onChange: (value: number) => void; value: number | null }) {
  const ctx = useField();
  return <RatingInput aria-labelledby={ctx?.labelId} onChange={onChange} value={value} />;
}

export function ReviewModal({
  close,
  score,
  body,
  onScoreChange,
  onBodyChange,
  onSubmit,
  isPending,
  scoreError,
  bodyError,
  showName,
  name,
  onNameChange,
}: ReviewModalProps) {
  const nameMissing = !!showName && !name?.trim();
  return (
    <>
      <Dialog.Header onClose={close} title={t("components.views.products.review_modal.title")} />
      <Dialog.Body>
        <div className="flex flex-col gap-5 px-1">
          {showName && (
            <StandardTextField
              isRequired
              label={t("components.views.products.review_modal.name_label")}
              onChange={onNameChange}
              placeholder={t("components.views.products.review_modal.name_placeholder")}
              value={name ?? ""}
            />
          )}
          <Field isInvalid={!!scoreError}>
            <Field.Label>{t("components.views.products.review_modal.rating_prompt")}</Field.Label>
            <RatingField onChange={onScoreChange} value={score} />
            {scoreError && <Field.Error>{scoreError}</Field.Error>}
          </Field>
          <StandardTextArea
            errorMessage={bodyError}
            isInvalid={!!bodyError}
            label={t("components.views.products.review_modal.review_label")}
            leadIcon={<Icon name="message" type="sticker" />}
            onChange={onBodyChange}
            placeholder={t("components.views.products.review_modal.review_placeholder")}
            value={body}
          />
        </div>
      </Dialog.Body>
      <Dialog.Footer>
        <ButtonDock
          primaryAction={
            <StandardButton color="pink" isDisabled={nameMissing} isPending={isPending} onPress={onSubmit} size="44">
              {t("common.actions.post")}
            </StandardButton>
          }
        />
      </Dialog.Footer>
    </>
  );
}
